import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './DetailDialog.css';

let openDialogs = 0, previousOverflow = '';
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function DetailDialog({ title, subtitle, theme = 'light', language = 'en', onClose, onBeforeClose, busy = false, children, className = '', style, allowEscapePropagation = false }) {
  const dialog = useRef(null), titleId = useId();
  const animation = useRef(null), mounted = useRef(false), closing = useRef(false);
  useEffect(() => {
    const node = dialog.current, previous = document.activeElement;
    mounted.current = true;
    closing.current = false;
    node.showModal?.();
    if (!node.open) node.setAttribute('open', '');
    if (openDialogs++ === 0) previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Start after showModal: a closed native dialog cannot paint its entrance.
    if (!reducedMotion()) animation.current = node.animate?.([
      { opacity: 0, transform: window.matchMedia?.('(max-width: 540px)').matches ? 'translateY(60px)' : 'translateY(20px) scale(.96)' },
      { opacity: 1, transform: 'translateY(0) scale(1)' },
    ], { duration: 260, easing: 'cubic-bezier(.2,.8,.2,1)' });
    return () => {
      mounted.current = false;
      animation.current?.cancel();
      node.close?.();
      // A parent overlay may unmount in the same commit and already unlock it.
      if (--openDialogs === 0 && document.body.style.overflow === 'hidden') document.body.style.overflow = previousOverflow;
      if (previous?.isConnected && (!previous.closest('dialog') || previous.closest('dialog').open)) previous.focus();
    };
  }, []);
  const close = afterClose => {
    if (busy || closing.current) return;
    if (onBeforeClose?.() === false) return;
    closing.current = true;
    const finish = () => {
      if (!mounted.current) return;
      onClose();
      if (typeof afterClose === 'function') afterClose();
    };
    const node = dialog.current;
    if (reducedMotion() || !node.animate) { finish(); return; }
    node.dataset.closing = 'true';
    animation.current?.cancel();
    animation.current = node.animate([
      { opacity: 1, transform: 'translateY(0) scale(1)' },
      { opacity: 0, transform: 'translateY(28px) scale(.98)' },
    ], { duration: 170, easing: 'ease-in', fill: 'forwards' });
    animation.current.finished.then(finish, () => {});
  };
  const containFocus = event => {
    if (event.target.closest('dialog') !== dialog.current) return;
    // Let an active drag sensor cancel with Escape without dismissing this dialog.
    if (event.key === 'Escape' && allowEscapePropagation) { event.preventDefault(); return; }
    // Older overlays listen on document; only the top dialog handles these keys.
    if (event.key === 'Escape' || event.key === 'Tab') event.stopPropagation();
    if (event.key !== 'Tab') return;
    const controls = [...dialog.current.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')].filter(node => node.getClientRects().length);
    if (!controls.length) { event.preventDefault(); dialog.current.focus(); return; }
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return createPortal(<dialog ref={dialog} style={style} className={`chat-detail-dialog ${theme === 'dark' ? 'is-dark' : ''} ${className}`} dir={language === 'ar' ? 'rtl' : 'ltr'} aria-labelledby={titleId} aria-modal="true"
    onKeyDown={containFocus} onCancel={event => { event.preventDefault(); event.stopPropagation(); close(); }} onClick={event => { if (event.target === event.currentTarget) close(); }}>
    <div className="chat-detail-content" aria-busy={busy}>
      <header className="chat-detail-heading"><div><h2 id={titleId}>{title}</h2>{subtitle && <small>{subtitle}</small>}</div><button type="button" className="chat-detail-icon" disabled={busy} onClick={() => close()} aria-label={language === 'ar' ? 'إغلاق' : 'Close'}><X size={20}/></button></header>
      {typeof children === 'function' ? children(close) : children}
    </div>
  </dialog>, document.body);
}
